import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { GolfActivityDto } from '@jordan/types';
import {
  ActionMenu,
  Alert,
  Breadcrumb,
  Button,
  DataTable,
  PageHeader,
  Spinner,
  type DataTableColumn,
} from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { apiDelete, apiGet } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';

const formatDate = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export function GolfActivityPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [activities, setActivities] = useState<GolfActivityDto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setErr(null);
    apiGet<GolfActivityDto[]>('/api/health/golf/activities')
      .then(setActivities)
      .catch(() => setErr('Failed to load activities.'));
  }, []);
  useEffect(load, [load]);

  const remove = useCallback(
    async (id: string) => {
      try {
        await apiDelete(`/api/health/golf/activities/${id}`);
        setActivities((a) => a?.filter((x) => x.id !== id) ?? null);
      } catch {
        setErr('Could not delete that activity.');
      }
    },
    [],
  );

  const columns = useMemo<DataTableColumn<GolfActivityDto>[]>(
    () => [
      { key: 'type', header: 'Type', sortable: true },
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        render: (a) => formatDate(a.date),
      },
      { key: 'shotCount', header: '# Shots', sortable: true, align: 'end' },
      { key: 'clubCount', header: '# Clubs', sortable: true, align: 'end' },
      {
        key: 'actions',
        header: '',
        align: 'end',
        render: (a) => (
          <span onClick={(e) => e.stopPropagation()}>
            <ActionMenu
              items={[
                { label: 'View summary', icon: 'ph-chart-line', onClick: () => navigate(`/health/golf/activity/${a.id}`) },
                { label: 'Delete', icon: 'ph-trash', danger: true, divider: true, onClick: () => void remove(a.id) },
              ]}
            />
          </span>
        ),
      },
    ],
    [navigate, remove],
  );

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">Activity</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Golf' }, { label: 'Activity' }]} />}
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center gap-2">
            <h5 className="mb-0 me-auto">Activities</h5>
            <Button variant="light" icon="ph-chart-scatter" onClick={() => navigate('/health/golf/activity/all')}>
              All-data summary
            </Button>
            <Button variant="primary" icon="ph-upload-simple" onClick={() => navigate('/health/golf/import')}>
              Import
            </Button>
          </div>
          <div className="card-body">
            {!activities ? (
              <Spinner />
            ) : (
              <DataTable
                columns={columns}
                rows={activities}
                getRowKey={(a) => a.id}
                searchable
                pageSize={15}
                onRowClick={(a) => navigate(`/health/golf/activity/${a.id}`)}
                initialSort={{ key: 'date', dir: 'desc' }}
                emptyMessage={
                  <>
                    No activities yet.{' '}
                    <a
                      href="/health/golf/import"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/health/golf/import');
                      }}
                    >
                      Import a CSV
                    </a>{' '}
                    to get started.
                  </>
                }
              />
            )}
          </div>
        </div>
      </div>
    </AppChrome>
  );
}
